import { downloadCsv } from "./downloadCsv";
import { toFormat } from "@/lib/dateHelpers";
import { capitalize } from "lodash";
import type {
  BeneficiaryRecord,
  CcmsAssociateBeneficiaryRecord,
  CcmsMemberInsuranceRecord
} from "../../../person/queries";

const MANAGED_IN_M360_PREFIX = "Managed in M360";

export const BENEFICIARIES_CSV_HEADERS = [
  "Source",
  "Record ID",
  "Name",
  "Beneficiary Type",
  "Relationship",
  "Percent",
  "Date of Birth",
  "SSN Last 4",
  "Program Type",
  "Status",
  "Effective Start",
  "Effective End",
  "Changed At",
  "Author",
  "Benef Line 1",
  "Benef Line 1 Name",
  "Benef Line 1 Relationship",
  "Benef Line 2",
  "Benef Line 2 Name",
  "Benef Line 2 Relationship",
  "Benef Line 3",
  "Benef Line 3 Name",
  "Benef Line 3 Relationship",
  "Benef Line 4",
  "Benef Line 4 Name",
  "Benef Line 4 Relationship",
  "Benef Line 5",
  "Benef Line 5 Name",
  "Benef Line 5 Relationship"
] as const;

export const BENEFICIARIES_CONTACT_CSV_HEADERS = [
  "Street 1",
  "Street 2",
  "City",
  "State",
  "Postal Code",
  "Country",
  "Phone",
  "Email"
] as const;

const BENEF_LINE_HEADER = "Benef Line 1";

export function beneficiariesCsvHeaders(includeContactInfo = false): string[] {
  if (!includeContactInfo) return [...BENEFICIARIES_CSV_HEADERS];

  const insertAt = BENEFICIARIES_CSV_HEADERS.indexOf(BENEF_LINE_HEADER);
  return [
    ...BENEFICIARIES_CSV_HEADERS.slice(0, insertAt),
    ...BENEFICIARIES_CONTACT_CSV_HEADERS,
    ...BENEFICIARIES_CSV_HEADERS.slice(insertAt)
  ];
}

export type BeneficiariesExportInput = {
  pgaId: string;
  beneficiaries: BeneficiaryRecord[];
  ccmsAssociateBeneficiaries: CcmsAssociateBeneficiaryRecord[];
  ccmsMemberInsuranceRecords: CcmsMemberInsuranceRecord[];
  includeContactInfo?: boolean;
};

const cell = (value: string | number | null | undefined): string => {
  if (value == null) return "";
  return String(value);
};

const formatDate = (value: string | null | undefined): string => (value ? (toFormat(value) ?? "") : "");

const formatDateTime = (value: string | null | undefined): string =>
  value ? (toFormat(value, "LLL dd, yyyy h:mm a") ?? "") : "";

const emptyContactCells = (): string[] => Array.from({ length: BENEFICIARIES_CONTACT_CSV_HEADERS.length }, () => "");

const m360ContactCells = (record: BeneficiaryRecord): string[] => [
  cell(record.address1),
  cell(record.address2),
  cell(record.city),
  cell(record.state),
  cell(record.postalCode),
  cell(record.country),
  cell(record.phone),
  cell(record.email)
];

export function isCcmsMemberRecordExcluded(record: CcmsMemberInsuranceRecord): boolean {
  return [record.benefLine1, record.benefLine1Name].some((value) => value?.trim().startsWith(MANAGED_IN_M360_PREFIX));
}

export function isCcmsAssociateRecordExcluded(record: CcmsAssociateBeneficiaryRecord): boolean {
  return record.fullName?.trim().startsWith(MANAGED_IN_M360_PREFIX) ?? false;
}

const m360Row = (record: BeneficiaryRecord, includeContactInfo: boolean): string[] => {
  const prefix = [
    "M360",
    cell(record.id),
    cell(record.fullName),
    capitalize(record.beneficiaryType),
    cell(record.relationship),
    record.percent != null ? `${record.percent}%` : "",
    formatDate(record.dateOfBirth),
    cell(record.ssnLast4),
    capitalize(record.applicationType),
    record.effectiveEndDate ? "Closed" : "Active",
    formatDate(record.effectiveStartDate),
    formatDate(record.effectiveEndDate),
    "",
    ""
  ];

  const contact = includeContactInfo ? m360ContactCells(record) : [];
  const benefLines = ["", "", "", "", "", "", "", "", "", "", "", "", "", "", ""];

  return [...prefix, ...contact, ...benefLines];
};

const ccmsAssociateRow = (record: CcmsAssociateBeneficiaryRecord, includeContactInfo: boolean): string[] => {
  const prefix = [
    "CCMS Associate",
    record.id,
    record.fullName ?? "",
    record.beneficiaryType ? capitalize(record.beneficiaryType) : "",
    record.relationship ?? "",
    record.percent != null ? `${record.percent}%` : "",
    formatDate(record.dateOfBirth),
    "",
    "",
    record.effectiveEndDate ? "Closed" : "Active",
    formatDate(record.effectiveStartDate),
    formatDate(record.effectiveEndDate),
    "",
    ""
  ];

  const contact = includeContactInfo ? emptyContactCells() : [];
  const benefLines = ["", "", "", "", "", "", "", "", "", "", "", "", "", "", ""];

  return [...prefix, ...contact, ...benefLines];
};

const memberLineFields = (record: CcmsMemberInsuranceRecord): string[] => [
  cell(record.benefLine1),
  cell(record.benefLine1Name),
  cell(record.benefLine1Relationship),
  cell(record.benefLine2),
  cell(record.benefLine2Name),
  cell(record.benefLine2Relationship),
  cell(record.benefLine3),
  cell(record.benefLine3Name),
  cell(record.benefLine3Relationship),
  cell(record.benefLine4),
  cell(record.benefLine4Name),
  cell(record.benefLine4Relationship),
  cell(record.benefLine5),
  cell(record.benefLine5Name),
  cell(record.benefLine5Relationship)
];

const ccmsMemberRow = (record: CcmsMemberInsuranceRecord, includeContactInfo: boolean): string[] => {
  const prefix = [
    "CCMS Member",
    record.id,
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    record.effectiveEndDate ? "Closed" : "Active",
    formatDate(record.effectiveStartDate),
    formatDate(record.effectiveEndDate),
    formatDateTime(record.changedAt),
    record.author?.trim() ?? ""
  ];

  const contact = includeContactInfo ? emptyContactCells() : [];

  return [...prefix, ...contact, ...memberLineFields(record)];
};

export function buildBeneficiariesCsvRows({
  beneficiaries,
  ccmsAssociateBeneficiaries,
  ccmsMemberInsuranceRecords,
  includeContactInfo = false
}: Omit<BeneficiariesExportInput, "pgaId">): string[][] {
  const rows: string[][] = [Array.from(beneficiariesCsvHeaders(includeContactInfo))];

  beneficiaries.forEach((record) => {
    rows.push(m360Row(record, includeContactInfo));
  });

  ccmsAssociateBeneficiaries
    .filter((record) => !isCcmsAssociateRecordExcluded(record))
    .forEach((record) => {
      rows.push(ccmsAssociateRow(record, includeContactInfo));
    });

  ccmsMemberInsuranceRecords
    .filter((record) => !isCcmsMemberRecordExcluded(record))
    .forEach((record) => {
      rows.push(ccmsMemberRow(record, includeContactInfo));
    });

  return rows;
}

export function beneficiariesCsvFilename(pgaId: string, date = new Date()): string {
  return `beneficiaries-${pgaId}-${date.toISOString().slice(0, 10)}.csv`;
}

export function exportBeneficiariesCsv(input: BeneficiariesExportInput): number {
  const rows = buildBeneficiariesCsvRows(input);
  downloadCsv(rows, beneficiariesCsvFilename(input.pgaId));
  return rows.length - 1;
}
